import { Component, ChangeDetectionStrategy, inject, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterLink, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FooterComponent } from '../footer/footer.component';
import { AuthService } from '../../services/auth.service';
import { CompanyInfoService } from '../../services/company-info.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-terms',
  standalone: true,
  imports: [CommonModule, RouterLink, FooterComponent],
  styleUrls: ['./terms.component.css'],
  template: `
    <main>
      <div class="header-container">
        <div class="back-link">
          <a (click)="goBack()" style="cursor: pointer; color: oklch(51% 0.28 263.83); font-size: 0.875rem;">← Zurück</a>
        </div>
        <div class="header-content">
          <a (click)="goHome()" style="cursor: pointer; text-decoration: none; display: inline-block;">
            <h1>ProTyco</h1>
            <p style="font-size: 0.875rem; text-align: center; margin: 0.25rem 0 0 0;">
              <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
            </p>
          </a>
        </div>
      </div>

      <div class="terms-scroll">
      <div class="container">
        <div class="content-card">
          <h1>Allgemeine Geschäftsbedingungen (AGB)</h1>
          
          <p class="last-updated">Gültig ab: 01. Januar 2026</p>

          <section class="terms-section">
            <h2>1. Geltungsbereich und Vertragsparteien</h2>
            <p>
              Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung der {{ (companyName$ | async) }}-Plattform 
              und die damit verbundenen Dienstleistungen. Vertragsparteien sind die {{ (companyName$ | async) }} 
              und der Nutzer der Plattform.
            </p>
            <p>
              Die Plattform ist ausschließlich für die Bewertung und Berechnung von Immobilien in Deutschland ausgelegt.
              Sämtliche Berechnungen, Steuersätze und rechtlichen Parameter basieren auf deutschem Immobilien‑ und Steuerrecht.
              Eine Nutzung für Immobilien außerhalb Deutschlands ist fachlich und technisch nicht vorgesehen, da hierfür keine ausreichende Datenbasis und keine rechtliche Grundlage vorliegt.
              Nutzer mit Wohnsitz außerhalb Deutschlands können die Plattform zwar aufrufen, jedoch für Immobilien außerhalb von Deutschland keine Leistungen sinnvoll nutzen, da der Service ausschließlich auf das deutsche Rechtssystem beschränkt ist.
            </p>
          </section>

          <section class="terms-section">
            <h2>2. Leistungsbeschreibung</h2>
            <p>
              {{ (companyName$ | async) }} bietet eine Online-Plattform zur Berechnung von Immobilienkosten und zur Analyse 
              von Immobilieninvestitionen. Die Plattform ermöglicht es Nutzern, verschiedene 
              Finanzierungsszenarien zu simulieren und Rentabilität von Immobilien zu berechnen.
            </p>
            <p>
              Die Plattform bildet ausschließlich deutsche Immobilienmärkte, deutsche Steuermodelle (z. B. Grunderwerbsteuer) sowie deutsche Nebenkostenstrukturen ab. Berechnungen für Immobilien außerhalb Deutschlands sind nicht möglich und nicht Bestandteil des Leistungsumfangs.
            </p>
          </section>

          <section class="terms-section">
            <h2>3. Registrierung und Nutzungsbedingungen</h2>
            <h3>1. Registrierung</h3>
            <p>
              Die Nutzung der {{ (companyName$ | async) }}-Plattform erfordert eine Registrierung. Der Nutzer 
              erklärt sich bereit, wahrheitsgemäße und vollständige Angaben zu machen.
            </p>
            <h3>2. Pflichten des Nutzers</h3>
            <p>
              Der Nutzer verpflichtet sich, die Plattform nur für rechtmäßige Zwecke zu nutzen und 
              keine Inhalte zu verbreiten, die strafbar sind oder gegen geltendes Recht verstoßen.
            </p>
            <h3>3. Passwort und Sicherheit</h3>
            <p>
              Der Nutzer ist dafür verantwortlich, sein Passwort sicher zu verwahren. Die {{ (companyName$ | async) }} 
              übernimmt keine Haftung für unbefugte Zugriffe auf das Nutzerkonto.
            </p>
          </section>

          <section class="terms-section">
            <h2>4. Nutzungsrechte an der Software</h2>
            <h3>1. Einfaches Nutzungsrecht</h3>
            <p>
              Der Nutzer erhält für den erworbenen Zeitraum ein einfaches, nicht übertragbares, nicht unterlizenzierbares Nutzungsrecht an der SaaS-Plattform.
            </p>
            <h3>2. Verbotene Handlungen</h3>
            <p>
              Dem Nutzer ist es untersagt:
            </p>
            <ul>
              <li>die Software zu kopieren, nachzubauen oder zu modifizieren,</li>
              <li>Sicherheitsmechanismen zu umgehen,</li>
              <li>die Plattform automatisiert auszulesen (Scraping, Bots),</li>
              <li>die Dienste zur Entwicklung konkurrierender Produkte zu verwenden,</li>
              <li>Ergebnisse maschinell oder datentechnisch weiterzuverarbeiten, wenn dies die Leistung nachbildet.</li>
            </ul>
            <h3>3. Rechte an Inhalten und Software</h3>
            <p>
              Alle Rechte an der Software, am Code, an Berechnungen, Methoden, Designs und Texten verbleiben vollständig bei {{ (companyName$ | async) }}.
            </p>
            <h3>4. Nutzung durch Dritte</h3>
            <p>
              Die Nutzung durch Dritte ist nur im Rahmen des eigenen Nutzerkontos erlaubt. Die Weitergabe der Zugangsdaten an andere Personen ist untersagt.
            </p>
            <h3>5. Geistiges Eigentum und Urheberrecht</h3>
            <p>
              Alle Inhalte der {{ (companyName$ | async) }}-Plattform, einschließlich Texte, Bilder, Videos und Logos, 
              sind urheberrechtlich geschützt. Der Nutzer darf diese Inhalte ohne ausdrückliche 
              Genehmigung nicht vervielfältigen, verbreiten oder ändern.
            </p>
          </section>

          <section class="terms-section">
            <h2>5. Service Level Agreement (SLA) und Verfügbarkeit</h2>
            <p>
              Die {{ (companyName$ | async) }} bemüht sich, die Plattform rund um die Uhr verfügbar zu halten. 
              Es können jedoch Ausfallzeiten für Wartungsarbeiten und Updates auftreten. 
              Die {{ (companyName$ | async) }} kann keine Gewähr für ununterbrochene Verfügbarkeit übernehmen.
            </p>
            <h3>1. Verfügbarkeit der Plattform</h3>
            <p>
              Die {{ (companyName$ | async) }} stellt die SaaS-Anwendung über Server eines externen Hosting-Anbieters (STRATO AG) bereit. Aus diesem Grund kann {{ (companyName$ | async) }} keine garantierte Mindestverfügbarkeit zusichern.
            </p>
            <p>
              Die Plattform wird jedoch nach bestem wirtschaftlich angemessenem Aufwand betrieben, um eine hohe Verfügbarkeit und störungsfreie Nutzung zu ermöglichen.
            </p>
            <h3>2. Wartungsarbeiten</h3>
            <p>
              Regelmäßige Wartungsarbeiten, Sicherheitsupdates und Systemoptimierungen können zu vorübergehenden Einschränkungen oder Unterbrechungen der Verfügbarkeit führen. Wartungen, die zu längeren Unterbrechungen führen, werden — sofern möglich — vorab angekündigt.
            </p>
            <h3>3. Nicht beeinflussbare Ausfälle</h3>
            <p>
              Ausfälle, die durch Dritte verursacht werden (z. B. Hosting-Anbieter, Netzbetreiber, KI-API-Anbieter, Internetprovider), gelten nicht als Verletzung der Leistungspflicht.
            </p>
            <h3>4. Support und Reaktionszeiten</h3>
            <p>
              Der Support erfolgt ausschließlich per E-Mail unter <a [href]="'mailto:' + (companyEmail$ | async)">{{ (companyEmail$ | async) }}</a>.
            </p>
            <p>
              Anfragen werden in der Regel innerhalb von 3–5 Werktagen beantwortet.
            </p>
            <p>
              Ein Anspruch auf bestimmte Reaktions- oder Lösungszeiten besteht nicht.
            </p>
            <h3>5. Leistungsunterbrechungen aufgrund höherer Gewalt</h3>
            <p>
              Ereignisse höherer Gewalt (z. B. Stromausfälle, DDoS-Angriffe, behördliche Eingriffe, Infrastrukturstörungen) berechtigen nicht zu Schadensersatzansprüchen.
            </p>
          </section>

          <section class="terms-section">
            <h2>6. Haftungsausschluss</h2>
            <p>
              Die Nutzung der {{ (companyName$ | async) }}-Plattform erfolgt auf Risiko des Nutzers. Die {{ (companyName$ | async) }} 
              übernimmt keine Garantie für die Richtigkeit, Vollständigkeit oder Aktualität der 
              berechneten Werte. Finanzielle Entscheidungen sollten nach sorgfältiger Prüfung und 
              idealerweise nach Beratung durch einen Fachmann getroffen werden.
            </p>
            <p>
              Insbesondere für Kalkulationen mit Unterstützung von künstlicher Intelligenz (Google Gemini API, Groq API) wird keine Haftung übernommen. Diese KI-gestützten Werte dienen nur zur Orientierung und sind nicht garantiert zuverlässig. Dies gilt insbesondere für Prognosen wie Immobilien-Wertsteigerung, Mieteinnahmevorhersagen und ähnliche KI-gestützte Berechnungen. Der Nutzer trägt allein die Verantwortung für finanzielle Entscheidungen.
            </p>

            <h3>1. Haftungsbeschränkung</h3>
            <p>
              Es wird unbeschränkt gehaftet für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit, die auf einer vorsätzlichen oder fahrlässigen Pflichtverletzung beruhen.<br>
              Für sonstige Schäden wird nur bei <strong>Vorsatz oder grober Fahrlässigkeit</strong> gehaftet.
            </p>
            <p>
              Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt. Kardinalpflichten sind solche Pflichten, deren Erfüllung die ordnungsgemäße Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der Kunde regelmäßig vertrauen darf.
            </p>

            <h3>2. Haftungsobergrenze (Begrenzung auf gezahlten Nutzungspreis)</h3>
            <p>
              Die Haftung ist – außer in Fällen von Vorsatz, grober Fahrlässigkeit oder Verletzung von Leben, Körper oder Gesundheit – der Höhe nach begrenzt auf den Betrag, den der Kunde in den letzten drei Monaten vor Eintritt des Schadensfalls für die Nutzung der SaaS-Software gezahlt hat.
            </p>
            <p>
              Bei einmaligen Entgelten ist die Haftung auf den jeweiligen Nettobetrag des Entgelts begrenzt.
            </p>

            <h3>3. Ausschluss von Folgeschäden</h3>
            <p>
              Es wird nicht gehaftet für indirekte oder mittelbare Schäden, insbesondere nicht für:
            </p>
            <ul>
              <li>entgangenen Gewinn,</li>
              <li>Produktions- oder Betriebsunterbrechungen,</li>
              <li>mittelbare Vermögensschäden,</li>
              <li>Wiederherstellungskosten oder Verlust von Daten,</li>
            </ul>
            <p>
              sofern diese nicht auf vorsätzlichem oder grob fahrlässigem Verhalten beruhen.
            </p>

            <h3>4. Verantwortung des Kunden für Datensicherung</h3>
            <p>
              Der Kunde ist verantwortlich für die regelmäßige, dem Risiko angemessene Sicherung seiner Daten.<br>
              Eine Haftung für Datenverluste besteht nur, wenn diese auf grob fahrlässigem Verhalten beruhen und die Daten selbst bei ordnungsgemäßer Datensicherung durch den Kunden verloren gegangen wären.
            </p>

            <h3>5. Höhere Gewalt</h3>
            <p>
              Es besteht keine Haftung für Ausfälle oder Leistungsstörungen, die auf höhere Gewalt oder Umstände zurückzuführen sind, die außerhalb des eigenen Einflussbereichs liegen. Dazu zählen insbesondere:
            </p>
            <ul>
              <li>Naturereignisse und behördliche Maßnahmen,</li>
              <li>Ausfälle von Telekommunikationsnetzen,</li>
              <li>Energie- und Stromausfälle,</li>
              <li>Angriffe wie DDoS-Attacken, sofern marktübliche Schutzmaßnahmen eingehalten wurden.</li>
            </ul>

            <h3>6. Geltung des Haftungsausschlusses</h3>
            <p>
              Diese Regelungen gelten im Rahmen der gesetzlichen Vorschriften, insbesondere gemäß §§ 305 ff. BGB. Zwingende gesetzliche Haftungsvorschriften bleiben unberührt.
            </p>

            <h3>7. Haftung für Inhalte</h3>
            <p>
              Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.
            </p>

            <h3>8. Haftung für Links</h3>
            <p>
              Unsere Website enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>

            <h3>9. Urheberrecht</h3>
            <p>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des Autors oder Urhebers.
            </p>
          </section>

          <section class="terms-section">
            <h2 id="widerrufsrecht">7. Widerrufsrecht</h2>
            
            <div class="withdrawal-policy" [innerHTML]="withdrawalPolicyHTML$ | async"></div>
          </section>

          <section class="terms-section">
            <h2>8. Zahlungsbedingungen</h2>
            <h3>1. Zahlungsarten</h3>
            <p>
              Die Zahlung erfolgt ausschließlich online über die auf der Website angebotenen Zahlungsanbieter (PayPal, Kreditkarte über Stripe oder vergleichbare Anbieter).
            </p>
            <h3>2. Zeitpunkt der Zahlung</h3>
            <p>
              Der vollständige Kaufpreis ist vor Freischaltung der Nutzung fällig.
            </p>
            <p>
              Eine Nutzung der Plattform ist erst nach bestätigtem Zahlungseingang möglich.
            </p>
            <h3>3. Kein Abonnementmodell</h3>
            <p>
              Der Erwerb der Nutzungslizenz erfolgt für einen fest ausgewählten Zeitraum, den der Nutzer beim Kauf bestimmt.
            </p>
            <p>
              Es handelt sich ausdrücklich nicht um ein Abonnement. Es erfolgt keine automatische Verlängerung und es bestehen keine wiederkehrenden Zahlungen.
            </p>
            <h3>4. Rückerstattungen</h3>
            <p>
              Bereits begonnene Nutzungszeiträume können nicht erstattet werden, es sei denn, es besteht ein gesetzliches Widerrufsrecht oder ein technischer Fehler im Verantwortungsbereich der {{ (companyName$ | async) }}.
            </p>
            <h3>5. Umsatzsteuerliche Behandlung</h3>
            <p>
              Für Nutzer außerhalb Deutschlands gelten die deutschen Umsatzsteuervorschriften.
              Unternehmenskunden aus EU‑Mitgliedstaaten mit gültiger Umsatzsteuer‑Identifikationsnummer werden nach dem Reverse‑Charge‑Verfahren gemäß Art. 196 MwStSystRL ohne deutsche Umsatzsteuer abgerechnet.
              Privatkunden aus EU‑Mitgliedstaaten werden mit deutscher Umsatzsteuer belastet, da der Leistungsort in Deutschland liegt.
            </p>
          </section>

          <section class="terms-section">
            <h2>9. Rechnungsstellung</h2>
            <p>
              Der Kunde erklärt sich damit einverstanden, dass Rechnungen ausschließlich in elektronischer Form gestellt werden.
            </p>
            <p>
              Die Rechnungen werden dem Kunden im Kundenkonto innerhalb der SaaS‑Applikation als PDF-Dokument zur Verfügung gestellt.
              Eine Rechnungsstellung per Post erfolgt nicht.
            </p>
            <p>
              Der Kunde wird per E‑Mail darüber informiert, sobald eine neue Rechnung im Kundenkonto verfügbar ist.
            </p>
            <p>
              Der Kunde ist verpflichtet, seine Rechnungsdaten auf Richtigkeit zu überprüfen.
            </p>
            <p>
              Nach Erstellung der Rechnung sind nachträgliche Änderungen der Rechnungsdaten nicht mehr möglich, es sei denn, es handelt sich um einen offensichtlichen Fehler und es wird eine korrigierte Rechnung erstellt.
            </p>
          </section>

          <section class="terms-section">
            <h2>10. Kündigungsbedingungen</h2>
            <h3>1. Ordentliche Kündigung</h3>
            <p>
              Da die Nutzungslizenz zeitlich befristet erworben wird und nicht automatisch verlängert wird, besteht keine ordentliche Kündigungsmöglichkeit.
            </p>
            <h3>2. Außerordentliche Kündigung</h3>
            <p>
              Die {{ (companyName$ | async) }} kann ein Nutzerkonto bei Verstoß gegen diese AGB oder geltendes Recht beenden.
            </p>
            <p>
              Beide Parteien können den Vertrag aus wichtigem Grund außerordentlich kündigen.
            </p>
            <p>
              Ein wichtiger Grund liegt insbesondere vor, wenn:
            </p>
            <ul>
              <li>der Nutzer die Plattform missbräuchlich, rechtswidrig oder sicherheitsgefährdend nutzt,</li>
              <li>der Nutzer gegen wesentliche Vertragspflichten verstößt,</li>
              <li>der Nutzer versucht, die Software zu manipulieren, zu kopieren oder unbefugt weiterzugeben.</li>
            </ul>
            <h3>3. Folgen der Kündigung</h3>
            <p>
              Nach wirksamer außerordentlicher Kündigung wird der Zugang zur Plattform gesperrt.
            </p>
            <p>
              Eine Erstattung des Kaufpreises erfolgt nur, wenn die {{ (companyName$ | async) }} den wichtigen Grund zu vertreten hat.
            </p>
          </section>

          <section class="terms-section">
            <h2>11. Änderungen der AGB</h2>
            <p>
              Die {{ (companyName$ | async) }} behält sich das Recht vor, diese AGB jederzeit zu ändern. 
              Der Nutzer wird über wesentliche Änderungen informiert. Die fortgesetzte Nutzung 
              der Plattform nach Änderungen gilt als Akzeptanz der neuen Bedingungen.
            </p>
          </section>

          <section class="terms-section">
            <h2>12. Anwendbares Recht und Gerichtsstand</h2>
            <p>
              Diese AGB unterliegen dem Recht der Bundesrepublik Deutschland. Gerichtsstand ist Berlin.
            </p>
          </section>

          <section class="terms-section">
            <h2>13. Datenschutz</h2>
            <p>
              Die {{ (companyName$ | async) }} verarbeitet personenbezogene Daten der Nutzer gemäß der geltenden 
              Datenschutzgesetze und ihrer Datenschutzerklärung.
            </p>
            <p>
              Die Datenschutzerklärung ist nicht Bestandteil dieser AGB, wird jedoch ergänzend angewendet.
              Durch Nutzung der Plattform bestätigt der Nutzer, die Datenschutzerklärung zur Kenntnis genommen zu haben und erklärt sich mit der 
              Verarbeitung seiner Daten gemäß dieser Bedingungen einverstanden.
            </p>
            <p>
              Weitere Informationen finden Sie in unserer 
              <a routerLink="/privacy">Datenschutzerklärung</a>.
            </p>
          </section>

          <section class="terms-section">
            <h2>14. Kontakt für Datenschutz- und AGB-Fragen</h2>
            <p>
              Bei Fragen zu diesen AGB oder zum Datenschutz kontaktieren Sie uns bitte per E-Mail: <a [href]="'mailto:' + (companyInfo$ | async)?.email">{{ (companyInfo$ | async)?.email }}</a>
            </p>
          </section>
        </div>
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
    }

    .terms-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .back-link {
      position: absolute;
      left: 5px;
      top: 37px;
      z-index: 10;
      pointer-events: none;
    }

    .back-link a {
      pointer-events: auto;
    }

    .back-link a:hover {
      opacity: 0.7;
      text-decoration: underline;
    }

    .back-link a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .header-content {
      flex: 1;
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
    }

    .header-content p span {
      position: relative;
    }

    .header-content a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .container {
      flex: 1;
      max-width: 900px;
      margin: 0 auto;
      width: 100%;
    }

    .content-card {
      background-color: white;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      padding: 2rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .content-card h1 {
      margin-top: 0;
      color: #1f2937;
      border-bottom: 2px solid oklch(51% 0.28 263.83);
      padding-bottom: 1rem;
    }

    .last-updated {
      color: #6b7280;
      font-size: 0.875rem;
      margin-top: -1rem;
      margin-bottom: 2rem;
    }

    .terms-section {
      margin-bottom: 2rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .terms-section:last-child {
      border-bottom: none;
    }

    .terms-section h2 {
      color: oklch(51% 0.28 263.83);
      margin-top: 0;
      margin-bottom: 0.75rem;
    }

    .terms-section h3 {
      color: #374151;
      margin-top: 1.5rem;
      margin-bottom: 0.5rem;
      font-size: 1.1rem;
    }

    .terms-section p {
      color: #6b7280;
      line-height: 1.6;
      margin: 0.5rem 0;
    }

    .terms-section ul,
    .terms-section ol {
      color: #6b7280;
    }

    .terms-section li {
      color: #6b7280;
    }

    .terms-section a {
      color: oklch(51% 0.28 263.83);
      text-decoration: none;
    }

    .terms-section a:hover {
      text-decoration: underline;
    }

    /* Widerrufsrecht HTML aus Datenbank */
    .withdrawal-policy {
      color: #9ca3af !important;
    }

    .withdrawal-policy h3 {
      color: #374151 !important;
      margin-top: 1.5rem !important;
      margin-bottom: 0.5rem !important;
      font-size: 1.1rem !important;
    }

    .withdrawal-policy p {
      line-height: 1.6 !important;
      margin: 0.5rem 0 !important;
    }

    .withdrawal-policy strong {
      font-weight: 600;
    }

    .withdrawal-policy ul {
      margin: 0.5rem 0 !important;
      padding-left: 1.5rem;
    }

    .withdrawal-policy li {
      line-height: 1.6 !important;
      margin: 0.25rem 0 !important;
    }

    .withdrawal-policy a {
      color: oklch(51% 0.28 263.83) !important;
      text-decoration: none;
    }

    .withdrawal-policy a:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      main {
        padding: 0 0 1rem;
        gap: 0;
      }

      .terms-scroll {
        padding: 22px 1rem 0;
      }

      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem !important;
        margin: 32px 0 0 15px !important;
      }

      .content-card {
        padding: 1rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsComponent implements AfterViewInit {
  private location = inject(Location);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private configService = inject(ConfigService);
  private sanitizer = inject(DomSanitizer);
  companyInfoService = inject(CompanyInfoService);
  companyInfo$ = this.companyInfoService.companyInfo$;
  companyName$ = this.configService.getConfig('Company:Name');
  companyEmail$ = this.configService.getConfig('Company:Email');

  withdrawalPolicyHTML$: Observable<SafeHtml> = combineLatest([
    this.companyInfo$,
    this.configService.getConfig('AGB:Widerrufsrecht')
  ]).pipe(
    map(([companyInfo, withdrawalPolicyRaw]) => {
      if (!withdrawalPolicyRaw) {
        return this.sanitizer.bypassSecurityTrustHtml('');
      }

      // Platzhalter interpolieren
      let interpolated = withdrawalPolicyRaw
        .replace(/\{\{email\}\}/g, companyInfo.email)
        .replace(/\{\{name\}\}/g, companyInfo.name)
        .replace(/\{\{street\}\}/g, companyInfo.street)
        .replace(/\{\{postalCode\}\}/g, companyInfo.postalCode)
        .replace(/\{\{city\}\}/g, companyInfo.city)
        .replace(/\{\{country\}\}/g, companyInfo.country);

      // HTML sanitizen und returnen
      return this.sanitizer.bypassSecurityTrustHtml(interpolated);
    })
  );

  ngAfterViewInit() {
    setTimeout(() => {
      // Check if there's a fragment in the URL
      this.activatedRoute.fragment.subscribe(fragment => {
        if (fragment) {
          // Try to find the element with the matching id
          const element = this.elementRef.nativeElement.querySelector(`#${fragment}`);
          if (element) {
            // Scroll to the element
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          // No fragment, scroll to top
          window.scrollTo(0, 0);
          const scrollElement = this.elementRef.nativeElement.querySelector('.terms-scroll');
          if (scrollElement) {
            scrollElement.scrollTop = 0;
          }
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      });
    }, 100);
  }

  goBack() {
    this.location.back();
  }

  goHome() {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/calculator']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
